import { msToServiceHour } from './timelineOutletFilter';
import { ribbonXAtHour } from './ribbonTimeAxis';
import type {
  ForecastData,
  ForecastPoint,
  ServiceTimelineOps,
  ServiceTimelineShift,
} from '../types';

export type PositionedShift = {
  id: string;
  name: string;
  startH: number;
  endH: number;
  x1: number;
  x2: number;
};

/** BigQuery-backed shift windows from the Live payload (no GraphQL fallback). */
export function resolveTimelineShifts(
  timeline: ServiceTimelineOps | null | undefined,
): ServiceTimelineShift[] {
  return timeline?.shifts?.length ? timeline.shifts : [];
}

/** First shift start → last shift end on the service-hour axis. */
export function serviceWindowFromShifts(
  shifts: ServiceTimelineShift[] | null | undefined,
  fallbackOpenH: number,
  fallbackCloseH: number,
): { openH: number; closeH: number } | null {
  if (!shifts?.length) return null;
  let openH = Infinity;
  let closeH = -Infinity;
  for (const s of shifts) {
    if (s.startMs == null || s.endMs == null) continue;
    let startH = msToServiceHour(s.startMs, fallbackOpenH, fallbackCloseH);
    let endH = msToServiceHour(s.endMs, fallbackOpenH, fallbackCloseH);
    if (endH <= startH) endH += 24;
    openH = Math.min(openH, startH);
    closeH = Math.max(closeH, endH);
  }
  if (!Number.isFinite(openH) || !Number.isFinite(closeH) || closeH <= openH) {
    return null;
  }
  return { openH, closeH };
}

/** Edge label for opens/closes (e.g. 11:30, 22h). */
export function formatServiceEdgeHour(hour: number): string {
  const wrapped = ((hour % 24) + 24) % 24;
  const hr = Math.floor(wrapped + 1e-9);
  const min = Math.round((wrapped - hr) * 60);
  if (min <= 0) return `${hr}h`;
  if (min >= 60) return `${(hr + 1) % 24}h`;
  return `${hr}:${String(min).padStart(2, '0')}`;
}

/** Pin forecast open/close (+ pts span) to active shift windows when present. */
export function alignForecastToShifts(
  fc: ForecastData,
  shifts: ServiceTimelineShift[] | null | undefined,
): ForecastData {
  const win = serviceWindowFromShifts(shifts, fc.openH, fc.closeH);
  if (!win) return fc;
  const { openH, closeH } = win;
  const byH = new Map(fc.pts.map(p => [p.h, p]));
  const pts: ForecastPoint[] = [];
  const startBucket = Math.floor(openH);
  const endBucket = Math.ceil(closeH);
  for (let h = startBucket; h <= endBucket; h++) {
    const existing =
      byH.get(h) ?? byH.get(((h % 24) + 24) % 24) ?? null;
    pts.push(
      existing
        ? { ...existing, h }
        : {
            h,
            occ: 0,
            covers: 0,
            guests: 0,
            predicted: h + 0.5 > fc.nowH,
          },
    );
  }
  if (pts.length < 2) return { ...fc, openH, closeH };
  return { ...fc, openH, closeH, pts };
}

/** Map shift windows onto the shared ribbon X axis (clamped to open→close). */
export function positionShifts(
  shifts: ServiceTimelineShift[] | null | undefined,
  openH: number,
  closeH: number,
  padL: number,
  padR: number,
  width: number,
): PositionedShift[] {
  if (!shifts?.length || closeH <= openH) return [];
  const out: PositionedShift[] = [];
  for (const s of shifts) {
    if (s.startMs == null || s.endMs == null) continue;
    let startH = msToServiceHour(s.startMs, openH, closeH);
    let endH = msToServiceHour(s.endMs, openH, closeH);
    if (endH <= startH) endH += 24;
    const clampedStart = Math.max(startH, openH);
    const clampedEnd = Math.min(endH, closeH);
    if (clampedEnd - clampedStart < 0.08) continue;
    const x1 = ribbonXAtHour(clampedStart, openH, closeH, padL, padR, width);
    const x2 = ribbonXAtHour(clampedEnd, openH, closeH, padL, padR, width);
    if (x2 - x1 < 4) continue;
    out.push({
      id: s.id,
      name: s.name || 'Shift',
      startH: clampedStart,
      endH: clampedEnd,
      x1,
      x2,
    });
  }
  return out;
}
